import { useMemo } from "react";

import useScopedMetrics from "./useScopedMetrics";
import { UseUsersDictionary, yetiBucketForWindow } from "./company";
import { beatYetiRateOf, beatYetiByWorkspace } from "../helpers/beatYeti";

/**
 * Beat the Yeti rate — how much of the team is ahead of the Yeti right now.
 *
 *   % beating the Yeti = (players beating the Yeti ÷ total active players) × 100
 *
 * The question is asked PER PLAYER, the same way the Beat the Yeti card and the
 * leaderboard's mountain ask it: a player's full points total for the window
 * against the Yeti's paced total for that same window. Two rules follow from
 * that and are the whole reason this is not a one-liner over `yeti.month.total`:
 *
 *   · Each player is measured against their OWN workspace's Yeti
 *     (`yetiByWs`), never the scope-wide average/sum — on a combined
 *     Field + In-House card the averaged pace belongs to neither team.
 *   · The divisor is the LIVE BOARD ROSTER in scope, not every head in the
 *     company: archived, inactive, the Yeti itself and admins are all off the
 *     leaderboard already, so they cannot sit in this rate either. And only
 *     players with an actual score count (helpers/beatYeti hasActualScore) —
 *     the score feed writes an all-zero placeholder row for someone who has not
 *     scored, and that is not a player who is behind the Yeti.
 *
 * The amount is returned as a 0–1 fraction, which is how the app stores every
 * rate (Scorekeeper, Closed Rate, the `%` tiles) — CustomWidget's own formatter
 * multiplies by 100 to print it.
 */

/**
 * @param {string[]} workspaceIds  the scope — pass `[]` to keep the hook idle
 *   (`useScopedMetrics` fetches nothing for an empty scope, which is how this
 *   stays free on every card that has not picked the metric).
 * @param {string}   window        scoped-metrics window id ("month", "week", …)
 * @param {object}   custom        { from, to } when window === "custom"
 * @param {string[]} teamIds       workspaces to break the count out for
 *   ("Technicians 7/10"). They are fetched alongside the scope in the SAME
 *   read, so the headline rate stays about `workspaceIds` only while each team
 *   here gets its own beating / players / rate in `byWorkspace`.
 */
export default function useBeatYetiRate(workspaceIds, window, custom, teamIds = []) {
  const usersDict = UseUsersDictionary();
  const ids = useMemo(
    () => (workspaceIds || []).filter(Boolean).map(String),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(workspaceIds || []).filter(Boolean).map(String).join("|")],
  );
  const teams = useMemo(
    () => [...new Set((teamIds || []).filter(Boolean).map(String))],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(teamIds || []).filter(Boolean).map(String).join("|")],
  );
  /* An idle scope stays idle even if teams are picked: no metric, no fetch. */
  const fetchIds = useMemo(
    () => (ids.length ? [...new Set([...ids, ...teams])] : []),
    [ids, teams],
  );
  const win = window || "month";
  /* Same window/scope the calling card already reads, so the score fetch is a
     cache hit on `fetchWorkspaceScores` rather than a second round trip. */
  const scoped = useScopedMetrics(fetchIds, { window: win, custom: custom || null, show: "avg" });

  return useMemo(() => {
    const bucketKey = yetiBucketForWindow(win === "year" ? "ytd" : win);
    /* `useScopedMetrics` already drops archived / inactive / Yeti rows; admins
       it keeps (they matter to a team AVERAGE's fetch, not to the board), so
       they come off here — the leaderboard and the mountain both exclude them,
       and an admin who never scores would otherwise dilute the rate. */
    const everyone = (scoped.people || []).filter((p) => {
      const u = usersDict?.[p?.userId] || usersDict?.[String(p?.userId)];
      return !(u && u.isAdmin);
    });
    /* The headline is about the card's own scope; a team fetched only so it
       could be broken out must not move it. */
    const inScope = new Set(ids);
    const roster = everyone.filter((p) => inScope.has(String(p?.wsId)));
    const { beating, players, rate } = beatYetiRateOf(roster, scoped.yetiByWs, bucketKey);
    return {
      amount: rate,
      beating,
      players,
      byWorkspace: beatYetiByWorkspace(everyone, scoped.yetiByWs, bucketKey, teams),
      loading: !!scoped.loading,
      refreshing: !!scoped.refreshing,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoped.people, scoped.yetiByWs, scoped.loading, scoped.refreshing, usersDict, win, ids, teams]);
}
